/**
 * The colour themes a child can choose between.
 *
 * **Given, never earned.** Every theme is available from the first minute.
 * None of them unlocks, costs points, or arrives as a reward.
 *
 * That distinction is the whole reason this file exists rather than being a
 * bit of decoration. Self-determination theory names autonomy — *I chose
 * this* — as one of three basic needs, and the same authors' meta-analysis
 * found that attaching a reward to an activity makes the activity itself less
 * interesting (d = −0.36 for rewards tied to finishing something). The same
 * feature wired two ways produces opposite effects: freely available it
 * supports autonomy; unlocked at 500 points it is the Epic! mechanic, and it
 * costs more motivation than it buys.
 * See research/engagement-and-reviews.md section 8.1.
 *
 * **Five, not thirty.** Choice overload measurably degrades the decisions
 * people make afterwards, and the child is about to spend twenty minutes
 * making decisions. Five is enough to feel like a choice and few enough to
 * make in two seconds.
 *
 * **Every theme keeps the same meanings.** Green is always "right", red is
 * always "not yet", the warm colour is always the child's own part of the app
 * and the cool one is always the grown-up's. A theme changes how the app
 * feels, never what a colour means — otherwise switching themes means
 * relearning the interface.
 */

/** Sprout — the original, from the approved wireframes. */
export const SPROUT = {
  // page and surfaces
  bg: '#FAF8F4',
  card: '#FFFFFF',
  cardWarm: '#FBFAF7',

  // text, darkest to lightest
  text: '#15202B',
  text2: '#31404E',
  muted: '#55636F',
  faint: '#8A94A0',
  fainter: '#97A1AC',

  // lines
  border: '#D8DFE7',
  borderWarm: '#E5DFD5',
  borderSoft: '#E4EAF1',

  // the parent's blue — sign-in, setup, settings
  blue: '#2563EB',
  blueDark: '#1D4ED8',
  blueTint: '#C6DAF6',
  bluePale: '#DBEAFE',
  blueFaint: '#EEF3FB',

  // the child's orange — anything inside a session
  orange: '#E8833A',
  orangeDark: '#A3571B',
  orangePale: '#FDF3EC',
  orangeTint: '#FDEBD8',
  orangeWarm: '#FBEADC',

  // right answers, growth, the garden
  green: '#7A9A5B',
  greenDark: '#4A6135',
  greenDarker: '#3E4F30',
  greenPale: '#F1F7EC',

  // wrong answers. Used sparingly — a mistake is information, not a telling-off.
  red: '#B02A2A',
  redPale: '#FBEDED',

  // illustration
  sand: '#E8DCC4',
  sandDark: '#A8875A',
  deep: '#2E2140',
} as const

export type Palette = { -readonly [K in keyof typeof SPROUT]: string }

export type ThemeId = 'sprout' | 'ocean' | 'sunset' | 'berry' | 'midnight'

export type ThemeMeta = {
  id: ThemeId
  /** What a child sees. Named for the feeling, not the hex. */
  label: string
  emoji: string
  /** The dots on the picker swatch: warm, cool, paper. */
  swatch: [string, string, string]
  palette: Palette
}

/**
 * Build a theme by changing only what should change.
 *
 * Everything not overridden falls through to Sprout, which keeps the contrast
 * decisions intact rather than asking each theme to get them right again.
 */
const from = (over: Partial<Palette>): Palette => ({ ...SPROUT, ...over })

/** Cool and calm. Teal where the green was, coral for the child's own work. */
const OCEAN = from({
  bg: '#F2F8FA', cardWarm: '#F7FBFC',
  border: '#CFE2E8', borderWarm: '#D4E6EC', borderSoft: '#E1EFF3',

  blue: '#0E7490', blueDark: '#0B5C72',
  blueTint: '#A9D8E4', bluePale: '#D6EDF4', blueFaint: '#EAF6F9',

  orange: '#E4564B', orangeDark: '#8F3029',
  orangePale: '#FDF0EE', orangeTint: '#FBDFDB', orangeWarm: '#FAE3DF',

  green: '#1F8578', greenDark: '#136259', greenDarker: '#0E4A43',
  greenPale: '#E7F5F3',

  sand: '#CFE3EA', sandDark: '#5E8A99', deep: '#14323F',
})

/** Warm and bright. The loudest of the five. */
const SUNSET = from({
  bg: '#FFF7F0', cardWarm: '#FFFAF5',
  border: '#F0D9C6', borderWarm: '#F3E0D0', borderSoft: '#F7E9DD',

  blue: '#B8430C', blueDark: '#8F3009',
  blueTint: '#FBCFAE', bluePale: '#FDE4D1', blueFaint: '#FEF2E8',

  orange: '#D9480C', orangeDark: '#8F3009',
  orangePale: '#FFF1E6', orangeTint: '#FFDFC7', orangeWarm: '#FFE6D2',

  green: '#8A6108', greenDark: '#63450A', greenDarker: '#4A3408',
  greenPale: '#FEF6E0',

  sand: '#F5D9B0', sandDark: '#B07C3F', deep: '#42210B',
})

/** Purple and pink — the one most often missing from children's apps, and
 *  then asked for. */
const BERRY = from({
  bg: '#FAF5FC', cardWarm: '#FDF9FE',
  border: '#E4D3EC', borderWarm: '#E9DCF0', borderSoft: '#F0E6F5',

  blue: '#7E22CE', blueDark: '#6B21A8',
  blueTint: '#DDBDF3', bluePale: '#EFDDFA', blueFaint: '#F8F0FD',

  orange: '#C2185B', orangeDark: '#8C1144',
  orangePale: '#FDF0F6', orangeTint: '#FBD9E9', orangeWarm: '#FADFEB',

  green: '#5C8A3C', greenDark: '#3F6028', greenDarker: '#334D20',
  greenPale: '#F1F7EC',

  sand: '#E7D6EE', sandDark: '#8D6BA0', deep: '#2E1140',
})

/**
 * The dark one.
 *
 * Worth having beyond taste: a session at eight in the evening on a bright
 * white screen is a different experience from the same session at four in the
 * afternoon. It is also the only theme whose text colours had to be rebuilt
 * rather than inherited — the others all assume dark text on light paper, and
 * inheriting that here would make every word invisible.
 */
const MIDNIGHT = from({
  bg: '#141A22', card: '#1D2530', cardWarm: '#222B37',

  text: '#F2F5F8', text2: '#DCE3EA', muted: '#A7B4C2',
  faint: '#8E9DAC', fainter: '#7C8B99',

  border: '#33404E', borderWarm: '#3A4757', borderSoft: '#2C3846',

  blue: '#60A5FA', blueDark: '#93C5FD',
  blueTint: '#2F4A6B', bluePale: '#1E3A5F', blueFaint: '#1A2C42',

  orange: '#FBA94C', orangeDark: '#FFD9A8',
  orangePale: '#2E2416', orangeTint: '#3D2F1B', orangeWarm: '#372A18',

  green: '#86C06C', greenDark: '#B9E0A4', greenDarker: '#D3EDC6',
  greenPale: '#1F2C1A',

  red: '#F87171', redPale: '#2E1A1A',

  sand: '#3A4757', sandDark: '#9FB0C0', deep: '#0B1017',
})

export const THEMES: ThemeMeta[] = [
  { id: 'sprout', label: 'Sprout', emoji: '🌱',
    swatch: ['#E8833A', '#7A9A5B', '#FAF8F4'], palette: { ...SPROUT } },
  { id: 'ocean', label: 'Ocean', emoji: '🐳',
    swatch: ['#E4564B', '#1F8578', '#F2F8FA'], palette: OCEAN },
  { id: 'sunset', label: 'Sunset', emoji: '🌅',
    swatch: ['#D9480C', '#8A6108', '#FFF7F0'], palette: SUNSET },
  { id: 'berry', label: 'Berry', emoji: '🫐',
    swatch: ['#C2185B', '#7E22CE', '#FAF5FC'], palette: BERRY },
  { id: 'midnight', label: 'Midnight', emoji: '🌙',
    swatch: ['#FBA94C', '#86C06C', '#1D2530'], palette: MIDNIGHT },
]

export const DEFAULT_THEME: ThemeId = 'sprout'

export function metaFor(id: string | null | undefined): ThemeMeta {
  return THEMES.find(t => t.id === id) ?? THEMES[0]!
}

export function paletteFor(id: string | null | undefined): Palette {
  return metaFor(id).palette
}
